import type { Metadata } from "next";
import { HI_DYNAMIC_PATH_PATTERNS } from "./locale-twins.generated";
import { pathDisposition } from "@/lib/indexability";
import { SITE_URL } from "@/lib/schema";
import type { Locale } from "./locales";
import { locales, HINDI_SLUGS, localeSlugAliases } from "./locales";
import type { RegionalLocale } from "./regional-locales";
import { regionalLocaleLang, regionalPath } from "./regional-locales";
import { regionalEditionsFor } from "./regional-twins.generated";
import en from "./messages/en";
import hi from "./messages/hi";

import { OPEN_GRAPH_IMAGES } from "@/lib/seo/social-metadata";
const messages: Record<Locale, Record<string, import("./messages/en").PageMeta>> = { en, hi };

function withTrailingSlash(path: string): string {
  return path.endsWith("/") ? path : `${path}/`;
}

// Resolve a slug to its canonical path for `locale`, applying the per-locale
// alias map. Extracted so generateAlternates stays under the complexity-10
// ratchet; output is byte-identical to the prior inline logic (pinned by
// tests/seo-metadata.test.ts).
function canonicalPathFor(slug: string, locale: Locale): string {
  const resolved = (locale === "hi" ? localeSlugAliases.hi : localeSlugAliases.en)?.[slug] ?? slug;
  const base = locale === "hi" ? `/hi/${resolved}` : `/${resolved}`;
  return withTrailingSlash(base);
}

/**
 * Generate Next.js alternates (hreflang) for a page.
 * Pass `hasHindi: false` for pages without a Hindi version.
 *
 * `canonicalOverrideSlug` consolidates a DUPLICATE page onto a stronger one:
 * the page's canonical points at the winner's path AND the page exits the
 * hreflang cluster entirely (returns `{ canonical }` only, no `languages`).
 * A same-language duplicate must NOT emit hreflang into the winner's cluster —
 * that creates unconfirmed/half-paired annotations the winner never returns
 * (rule 27). Use this instead of a bare `alternates` override, which silently
 * strips `languages` via the `...overrides` spread in generatePageMetadata.
 */
export function generateAlternates(
  slug: string,
  locale: Locale,
  hasHindi = true,
  canonicalOverrideSlug?: string
): Metadata["alternates"] {
  if (canonicalOverrideSlug) {
    return { canonical: `${SITE_URL}${canonicalPathFor(canonicalOverrideSlug, locale)}` };
  }
  const enSlug = localeSlugAliases.en?.[slug] ?? slug;
  const hiSlug = localeSlugAliases.hi?.[slug] ?? slug;
  const canonicalPath = canonicalPathFor(slug, locale);

  // Emit absolute URLs. metadataBase normally absolutises alternates, but the
  // OpenNext Cloudflare renderer (next 16.2.6 + @opennextjs/cloudflare 1.19)
  // serves the relative form to the browser, costing 0.10 on Lighthouse SEO
  // (invalid canonical + invalid hreflang on /free-kundali/, /compatibility/,
  // /panchang/, /ai-astrologer/). Build the absolute URL here so there is no
  // resolution gap between Next render and edge serve.
  const canonical = `${SITE_URL}${canonicalPath}`;

  const languages: Record<string, string> = {
    en: `${SITE_URL}/${enSlug}/`,
    "x-default": `${SITE_URL}/${enSlug}/`,
    // Regional editions reciprocate automatically. Today every regional page is
    // unearned, so this contributes nothing and the en/hi output is unchanged;
    // the moment one EARNS indexing (rule 66) both directions of the annotation
    // appear in the same deploy, which is the half-pairing trap the docstring
    // above describes, closed by construction instead of by remembering.
    ...regionalLanguageEntries(enSlug),
  };
  // A Hindi render ALWAYS declares its own `hi` entry. `hasHindi` answers a
  // different question — "does the English slug have a Hindi twin", read from a
  // registry keyed by EN slug — so a Hindi page whose route key is the
  // Hindi-led slug (/hi/aaj-ka-rashifal/) or a dynamic nested one
  // (/hi/mantras/<graha>/) missed and shipped a cluster with NO self-reference.
  // Google discards a cluster whose members do not list themselves, and the
  // x-default then pointed every locale at the English page. Rendering the
  // Hindi page is itself the proof its Hindi URL exists, so the self-reference
  // must not depend on registry membership.
  //
  // An ENGLISH render names the twin only while the twin is INDEXABLE. A
  // governed `noindex,follow` twin (rule-76 sink, unearned literal, quarantine)
  // is an unconfirmed pair — Google drops the annotation and can distrust the
  // rest of the cluster (rule 27 #5) — so the `hi` entry reads the same
  // `pathDisposition()` contract the twin's own robots meta reads, exactly as
  // `regionalLanguageEntries` below already does for regional editions (rule
  // 94). Re-promoting the twin restores the annotation by construction; on
  // 2026-09-02 four live English pages annotated demoted Hindi twins.
  const hiPath = `/hi/${hiSlug}/`;
  if (locale === "hi" || (hasHindi && pathDisposition(hiPath).kind === "index")) {
    languages.hi = `${SITE_URL}${hiPath}`;
  }
  return { canonical, languages };
}

/**
 * Does `slug` have a Hindi twin that belongs in the English page's cluster?
 *
 * `HINDI_SLUGS` is derived from a scan of LITERAL `src/app/hi/**` directories,
 * so it is structurally blind to a Hindi twin served by a DYNAMIC segment:
 * `src/app/hi/mantras/[graha]/` publishes nine governed-indexed Hindi pages the
 * scan cannot see. `/mantras/<graha>/` therefore shipped an English-only cluster
 * while its live Hindi twin declared the pair — the half-paired annotation
 * Google drops. The governed dynamic-index projection already owns exactly which
 * dynamic routes are indexed, so read it rather than hand-listing the family
 * (rule 66: two registries covering one route may not disagree).
 */
function hasHindiTwin(slug: string): boolean {
  if (HINDI_SLUGS.has(slug)) return true;
  return HI_DYNAMIC_PATH_PATTERNS.some((pattern) =>
    pattern.test(localeSlugAliases.hi?.[slug] ?? slug)
  );
}

/**
 * hreflang entries for the regional editions that publish `slug`.
 *
 * Only INDEXABLE editions join the cluster. A `noindex` member is an
 * unconfirmed pair — Google drops the annotation and can distrust the rest of
 * the cluster with it (rule 27 #5) — so an edition enters its cluster exactly
 * when it earns indexing, and leaves if it is ever demoted. Membership is read
 * from the same `pathDisposition()` contract the page's own robots meta uses,
 * so the two signals cannot disagree.
 */
function regionalLanguageEntries(slug: string): Record<string, string> {
  const entries: Record<string, string> = {};
  for (const locale of regionalEditionsFor(slug)) {
    const path = regionalPath(locale, slug);
    if (pathDisposition(path).kind !== "index") continue;
    entries[regionalLocaleLang[locale]] = `${SITE_URL}${path}`;
  }
  return entries;
}

/**
 * The ONLY supported way to build a regional edition page's metadata.
 *
 * A regional page has no message bundle, so it cannot use
 * `generatePageMetadata`, and it needs an hreflang cluster, so
 * `seoPageMetadata` is not enough either. Before this helper existed the five
 * pages of the 2026-08-13 matrix each hand-rolled `export const metadata = {…}`
 * and every one of them shipped with no canonical, no hreflang and no `robots`
 * — indexable in violation of rule 66 while the registry said unearned.
 * `check-regional-page-metadata.mjs` now fails the build if a regional page
 * declares its metadata any other way.
 */
export function regionalPageMetadata(input: {
  locale: RegionalLocale;
  /** The en slug this edition translates — also its directory name. */
  slug: string;
  title: string;
  description: string;
  keywords?: string[];
}): Metadata {
  const { locale, slug, title, description, keywords } = input;
  const path = regionalPath(locale, slug);
  const canonical = `${SITE_URL}${path}`;
  const disposition = pathDisposition(path);

  const languages: Record<string, string> = {
    en: `${SITE_URL}/${slug}/`,
    "x-default": `${SITE_URL}/${slug}/`,
    // Same predicate as generatePageMetadata — a second copy of "does a Hindi
    // twin exist" is how the two halves of a cluster drift apart.
    ...(hasHindiTwin(slug)
      ? { hi: `${SITE_URL}/hi/${localeSlugAliases.hi?.[slug] ?? slug}/` }
      : {}),
    ...regionalLanguageEntries(slug),
  };

  return {
    title,
    description,
    ...(keywords ? { keywords } : {}),
    openGraph: {
      title,
      description,
      siteName: "Ishvaram",
      type: "website",
      url: canonical,
      images: OPEN_GRAPH_IMAGES,
    },
    // A one-member cluster is noise, not an annotation. When this edition is
    // unearned it is absent from `languages`, but en (and hi) are still real
    // alternates of it, so the cluster stays meaningful either way.
    alternates: { canonical, languages },
    ...(disposition.kind === "noindex"
      ? { robots: { index: false, follow: disposition.follow } }
      : {}),
  };
}

/**
 * Generate a complete Next.js Metadata object for a page.
 *
 * Usage:
 *   export const metadata = generatePageMetadata("panchang", "en");
 *   export const metadata = generatePageMetadata("panchang", "hi");
 *
 * The locale is usually known at compile time from the file's location:
 *   - /app/panchang/page.tsx      → locale "en"
 *   - /app/hi/panchang/page.tsx   → locale "hi"
 */
/**
 * Slugs whose <title> + description carry today's IST date. The date is read
 * when generatePageMetadata() runs, so a route using one of these slugs must
 * call it from generateMetadata() on a force-dynamic page. A module constant is
 * computed once per Worker isolate, and an ISR render never regenerates on this
 * deployment (open-next.config.ts wires a no-op revalidation queue):
 * /rashifal/ served Friday's date on Monday 28 Sep 2026 that way.
 */
const TIME_SENSITIVE_SLUGS = new Set([
  "panchang",
  "tithi-today",
  "choghadiya",
  "rashifal",
  "aaj-ka-rashifal",
  "rahu-kaal",
  "shubh-muhurat",
  "monthly-rashifal",
  "weekly-rashifal",
]);

/** True when generatePageMetadata(slug) stamps today's IST date into the head. */
export function hasDatedMetadata(slug: string): boolean {
  return TIME_SENSITIVE_SLUGS.has(slug);
}

// Computed once at module load. The daily Cloudflare deploy workflow
// guarantees these refresh on Jan 1.
const CURRENT_YEAR = new Date().getFullYear();
const NEXT_YEAR = CURRENT_YEAR + 1;
const PREV_YEAR = CURRENT_YEAR - 1;
const YEAR_2D = String(CURRENT_YEAR).slice(-2);

function applyYearTokens(s: string): string {
  return s
    .replace(/\{year\}/g, String(CURRENT_YEAR))
    .replace(/\{nextyear\}/g, String(NEXT_YEAR))
    .replace(/\{prevyear\}/g, String(PREV_YEAR))
    .replace(/\{year_2d\}/g, YEAR_2D);
}

const HI_WEEKDAYS = ["रविवार", "सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार"];
const HI_MONTHS = [
  "जनवरी",
  "फरवरी",
  "मार्च",
  "अप्रैल",
  "मई",
  "जून",
  "जुलाई",
  "अगस्त",
  "सितंबर",
  "अक्टूबर",
  "नवंबर",
  "दिसंबर",
];

// Exported so per-route generateMetadata() on the /today/ timing pages
// reuse the SAME IST date string the
// TIME_SENSITIVE_SLUGS root pages prefix — one source of truth, no drift
// (rule 23/24). Caller must keep the route's `revalidate` <= 3600 so the
// title date never lags past an hour.
export function formatDateForTitle(locale: Locale, d: Date = new Date()): string {
  const istParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(d);
  const values = Object.fromEntries(istParts.map((part) => [part.type, part.value]));
  const istDate = new Date(Number(values.year), Number(values.month) - 1, Number(values.day));

  if (locale === "hi") {
    return `${HI_WEEKDAYS[istDate.getDay()]}, ${istDate.getDate()} ${
      HI_MONTHS[istDate.getMonth()]
    } ${istDate.getFullYear()}`;
  }
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

export function generatePageMetadata(
  slug: string,
  locale: Locale,
  overrides?: Partial<Metadata>,
  canonicalOverrideSlug?: string
): Metadata {
  const msg = messages[locale]?.[slug];
  if (!msg) {
    console.warn(`[i18n] No message for slug "${slug}" in locale "${locale}"`);
  }

  const metadataText = localizedMetadataText(slug, locale, msg);
  const keywords = msg?.keywords?.map(applyYearTokens);
  const hasHindi = hasHindiTwin(slug);

  const baseOpenGraph: NonNullable<Metadata["openGraph"]> = {
    title: metadataText.ogTitle,
    description: metadataText.ogDescription,
    siteName: "Ishvaram",
    type: "website",
    images: OPEN_GRAPH_IMAGES,
  };

  return {
    title: metadataText.title,
    description: metadataText.description,
    ...(keywords ? { keywords } : {}),
    openGraph: baseOpenGraph,
    alternates: generateAlternates(slug, locale, hasHindi, canonicalOverrideSlug),
    ...overrides,
    // `overrides` is spread wholesale, and an `openGraph` key inside it REPLACES
    // the block above — including the share image. That is the same Next.js
    // replacement trap this helper exists to absorb (see OPEN_GRAPH_IMAGES), one
    // level up. Merge instead, so a caller can retitle its card without silently
    // dropping the image from the highest-traffic routes on the site.
    ...(overrides?.openGraph ? { openGraph: { ...baseOpenGraph, ...overrides.openGraph } } : {}),
    // The single indexability contract (ADR-0029) is authoritative and is applied
    // AFTER `overrides` so a per-page robots value can never re-index a governed
    // demotion. Literal app routes previously emitted no robots meta at all, so a
    // rule-76 impression sink stayed fully indexable even though pathDisposition()
    // had already demoted it — /hi/tithi-today/ (471K impr, 0.50% CTR) and
    // /tithi-today/ (168K impr, 0.81% CTR) were live in that state on 2026-07-26.
    // The D1 shell-renderer already honoured this; literal routes did not.
    ...robotsOverrideFor(slug, locale, canonicalOverrideSlug),
  };
}

/**
 * Derive the governed robots directive for a literal app route from the single
 * disposition contract. Returns `{}` when the path is indexable so Next keeps its
 * default (no robots meta = indexable), matching prior behaviour exactly for every
 * page that is not governed-noindex.
 */
function robotsOverrideFor(
  slug: string,
  locale: Locale,
  canonicalOverrideSlug?: string
): Pick<Metadata, "robots"> | Record<string, never> {
  const disposition = pathDisposition(canonicalPathFor(canonicalOverrideSlug ?? slug, locale));
  if (disposition.kind !== "noindex") return {};
  return { robots: { index: false, follow: disposition.follow } };
}

function localizedMetadataText(
  slug: string,
  locale: Locale,
  msg: (typeof messages)[Locale][string] | undefined
) {
  const title = msg?.title ?? `Ishvaram — ${slug}`;
  const description = msg?.description ?? "";
  const ogTitle = msg?.ogTitle ?? title;
  const ogDescription = msg?.ogDescription ?? description;
  const text = { title, description, ogTitle, ogDescription };
  const datedText = TIME_SENSITIVE_SLUGS.has(slug) ? withDateStamp(text, locale) : text;
  return applyYearToMetadataText(datedText);
}

/**
 * Daily pages carry today's IST date in their head metadata. The TITLE puts
 * the page's own name first and the date LAST: a date-first title spent the
 * first ~25 SERP characters on a string every daily page shares, so the
 * mobile snippet truncated the part that names the page (2026-09-08). The
 * description keeps the leading date — it is a sentence, not a label.
 * `edge-db/shared/rashifal-freshness.ts` re-dates the same fields on the R2
 * fallback render and must keep the same shape (title suffix, description
 * prefix), which its tests pin.
 */
function withDateStamp(
  text: { title: string; description: string; ogTitle: string; ogDescription: string },
  locale: Locale
) {
  const dateStr = formatDateForTitle(locale);
  return {
    title: `${text.title} — ${dateStr}`,
    description: `${dateStr}. ${text.description}`,
    ogTitle: `${text.ogTitle} — ${dateStr}`,
    ogDescription: `${dateStr}. ${text.ogDescription}`,
  };
}

function applyYearToMetadataText(text: {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
}) {
  return {
    title: applyYearTokens(text.title),
    description: applyYearTokens(text.description),
    ogTitle: applyYearTokens(text.ogTitle),
    ogDescription: applyYearTokens(text.ogDescription),
  };
}

/**
 * Check which locale slugs are missing translations.
 * Useful for auditing coverage before adding a new language.
 */
export function auditTranslations(): {
  locale: Locale;
  slug: string;
  missing: boolean;
}[] {
  const allSlugs = new Set([...Object.keys(en), ...Object.keys(hi)]);
  const results: { locale: Locale; slug: string; missing: boolean }[] = [];
  for (const locale of locales) {
    for (const slug of allSlugs) {
      results.push({
        locale,
        slug,
        missing: !messages[locale]?.[slug],
      });
    }
  }
  return results.filter((r) => r.missing);
}

export { detectLocale, getLocalePath, locales, defaultLocale } from "./locales";
export type { Locale };
